/**
 * Wires `cornerPin.ts` geometry to framer-motion so the assistant launcher
 * puck can be dragged anywhere and snaps to the nearest corner with
 * momentum on release. Also derives a live acceleration signal (via chained
 * `useVelocity`) that `LogoMarkPhysics` uses to jostle the two discs while
 * the puck is moving.
 */

import React from 'react';
import { animate, useMotionValue, useReducedMotion, useVelocity, type PanInfo } from 'framer-motion';
import {
  anchorFor,
  nearestCorner,
  projectRelease,
  PUCK_SIZE,
  TAKEOVER_PULSE_MAGNITUDE,
  type Corner,
  type Point,
} from './cornerPin';
import type { PendingImpulse } from './discPhysics';

/** Drag distance (px) beyond which a release is treated as a drag, not a click. */
const DRAG_THRESHOLD = 4;
/** Divides px/s^2 motion-value acceleration down into SVG (viewBox) units/s^2. */
const ACCEL_SCALE = 8;
/** Clamp so a violent fling can't blow up the disc physics. */
const ACCEL_CLAMP = 1200;
/** Magnitude of the landing "thunk" impulse applied to the discs, in SVG units/s. */
const LANDING_IMPULSE = 25;
const SPRING_TRANSITION = { type: 'spring' as const, stiffness: 260, damping: 26 };
/**
 * The captive-takeover flight's own spring — a touch more overshoot than the
 * drag-release snap (`SPRING_TRANSITION`) so a programmatic flight reads as
 * a deliberate flourish rather than an input-driven settle: ~350ms to rest
 * with one soft overshoot past the target.
 */
const FLIGHT_SPRING = { type: 'spring' as const, stiffness: 170, damping: 22, mass: 1 };
/** Wind-up-to-launch delay — the squeeze-then-release beat before a flight. */
const WINDUP_DELAY_MS = 120;
/** Delay between the landing thunk and the outward "arrived" pop. */
const POP_DELAY_MS = 60;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getViewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}

export function useCornerPin(initialCorner: Corner, onCornerChange: (corner: Corner) => void) {
  const reducedMotion = useReducedMotion();
  // The pinned corner is owned by user settings (AppShell) — this ref is only
  // the copy the resize handler needs, since it runs outside React's render.
  const cornerRef = React.useRef<Corner>(initialCorner);

  const initialAnchorRef = React.useRef<Point | null>(null);
  if (initialAnchorRef.current === null) {
    initialAnchorRef.current = anchorFor(initialCorner, getViewport(), PUCK_SIZE);
  }

  const x = useMotionValue(initialAnchorRef.current.x);
  const y = useMotionValue(initialAnchorRef.current.y);

  // Acceleration = velocity of velocity, chained per framer-motion's own
  // recommended pattern for deriving higher-order motion signals.
  const vx = useVelocity(x);
  const vy = useVelocity(y);
  const ax = useVelocity(vx);
  const ay = useVelocity(vy);

  const accelRef = React.useRef<Point>({ x: 0, y: 0 });
  const activeRef = React.useRef(false);
  const didDragRef = React.useRef(false);
  const dragOriginRef = React.useRef<Point>({ x: 0, y: 0 });
  const impulseRef = React.useRef<PendingImpulse | null>(null);
  const [runToken, setRunToken] = React.useState(0);
  // Every `flyTo`/`returnToCorner` call claims the next number here. A call
  // that's been superseded (a newer `flyTo` started, e.g. a cancel firing
  // `returnToCorner` while the entrance flight is still in its wind-up
  // sleep) re-checks this at each await point and bails rather than playing
  // out a flight nobody wants anymore — see `flyTo` below.
  const flightSeqRef = React.useRef(0);

  // Fires the open/dismiss "pop" — a radial impulse, queued for the physics
  // loop and woken via runToken. Under reduced motion the loop never runs,
  // so queuing an impulse would strand it in the ref forever; no-op instead.
  const pulse = React.useCallback(
    (magnitude: number) => {
      if (reducedMotion) return;
      impulseRef.current = { kind: 'radial', magnitude };
      setRunToken((token) => token + 1);
    },
    [reducedMotion]
  );

  React.useEffect(() => {
    const unsubX = ax.on('change', (value) => {
      accelRef.current.x = clamp(value / ACCEL_SCALE, -ACCEL_CLAMP, ACCEL_CLAMP);
    });
    const unsubY = ay.on('change', (value) => {
      accelRef.current.y = clamp(value / ACCEL_SCALE, -ACCEL_CLAMP, ACCEL_CLAMP);
    });
    return () => {
      unsubX();
      unsubY();
    };
  }, [ax, ay]);

  // Reposition (no spring) when the viewport changes so the puck stays
  // anchored to its pinned corner.
  React.useEffect(() => {
    const onResize = () => {
      const anchor = anchorFor(cornerRef.current, getViewport(), PUCK_SIZE);
      x.set(anchor.x);
      y.set(anchor.y);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [x, y]);

  const onDragStart = React.useCallback(() => {
    didDragRef.current = false;
    activeRef.current = true;
    dragOriginRef.current = { x: x.get(), y: y.get() };
    setRunToken((token) => token + 1);
  }, [x, y]);

  const onDrag = React.useCallback(() => {
    if (didDragRef.current) return;
    const dx = x.get() - dragOriginRef.current.x;
    const dy = y.get() - dragOriginRef.current.y;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      didDragRef.current = true;
    }
  }, [x, y]);

  const onDragEnd = React.useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const viewport = getViewport();
      const released: Point = { x: x.get(), y: y.get() };
      const projected = projectRelease(released, info.velocity);
      const target = nearestCorner(projected, viewport, PUCK_SIZE);
      const anchor = anchorFor(target, viewport, PUCK_SIZE);

      // The x and y springs are independent and seeded with different
      // release velocities, so they settle at different times — land()
      // must wait for both, or the corner commits (and the puck "thunks")
      // while it's still visibly sliding on the axis that hasn't finished.
      // `completedAxes`/`landed` are local to this onDragEnd call, so an
      // axis callback firing after `landed` is already true (e.g. a
      // superseded spring still completing) is a harmless no-op.
      const requiredAxes = reducedMotion ? 1 : 2;
      let completedAxes = 0;
      let landed = false;

      const land = () => {
        completedAxes += 1;
        if (landed || completedAxes < requiredAxes) return;
        landed = true;

        activeRef.current = false;
        cornerRef.current = target;
        onCornerChange(target);
        // Thunk the discs opposite the direction the puck just arrived from.
        const dirX = anchor.x - released.x;
        const dirY = anchor.y - released.y;
        const mag = Math.hypot(dirX, dirY) || 1;
        impulseRef.current = {
          kind: 'translational',
          vector: { x: (-dirX / mag) * LANDING_IMPULSE, y: (-dirY / mag) * LANDING_IMPULSE },
        };
        setRunToken((token) => token + 1);
      };

      if (reducedMotion) {
        x.set(anchor.x);
        y.set(anchor.y);
        land();
        return;
      }

      animate(x, anchor.x, { ...SPRING_TRANSITION, velocity: info.velocity.x, onComplete: land });
      animate(y, anchor.y, { ...SPRING_TRANSITION, velocity: info.velocity.y, onComplete: land });
    },
    [onCornerChange, reducedMotion, x, y]
  );

  // Fly the puck to an arbitrary top-left point (not a pinned corner) — the
  // captive-takeover choreography's flight to the content area's center and
  // back. One continuous physical gesture, reusing the SAME disc-impulse
  // machinery a drag release uses rather than a parallel effect:
  //  - optional wind-up: squeeze the discs inward, hold a beat, launch on
  //    the rebound (`opts.windUp` — the entrance flight only; the return
  //    flight has nothing to wind up from, it's already mid-choreography);
  //  - `activeRef` stays true for the WHOLE call (not just the tween), so
  //    the physics loop in `LogoMarkPhysics` keeps consuming `accelRef`'s
  //    jostle through the wind-up hold and the post-landing pop delay, not
  //    just while the motion values are actively tweening;
  //  - on arrival: a landing thunk opposite the direction of travel (same
  //    gesture as a drag-release landing), then a delayed outward pop once
  //    the eye has registered the arrival.
  const flyTo = React.useCallback(
    async (point: Point, opts?: { windUp?: boolean; landingPopMagnitude?: number }): Promise<void> => {
      // Claim this call's sequence number. Any earlier `flyTo` still sleeping
      // through its wind-up (or its post-landing pop delay) checks this
      // after every await and bails the moment it sees a newer call has
      // started — e.g. a cancel's `returnToCorner` firing mid-wind-up must
      // not let the stale entrance flight spring the puck back out to
      // center once its 120ms sleep finally elapses.
      const seq = ++flightSeqRef.current;
      activeRef.current = true;
      if (reducedMotion) {
        x.set(point.x);
        y.set(point.y);
        if (flightSeqRef.current === seq) activeRef.current = false;
        return;
      }

      if (opts?.windUp) {
        pulse(-TAKEOVER_PULSE_MAGNITUDE);
        await new Promise<void>((resolve) => setTimeout(resolve, WINDUP_DELAY_MS));
        if (flightSeqRef.current !== seq) return;
      }

      const from: Point = { x: x.get(), y: y.get() };
      await new Promise<void>((resolve) => {
        let done = 0;
        const onDone = () => {
          done += 1;
          if (done === 2) resolve();
        };
        animate(x, point.x, { ...FLIGHT_SPRING, onComplete: onDone });
        animate(y, point.y, { ...FLIGHT_SPRING, onComplete: onDone });
      });
      if (flightSeqRef.current !== seq) return;

      const dirX = point.x - from.x;
      const dirY = point.y - from.y;
      const mag = Math.hypot(dirX, dirY) || 1;
      impulseRef.current = {
        kind: 'translational',
        vector: { x: (-dirX / mag) * LANDING_IMPULSE, y: (-dirY / mag) * LANDING_IMPULSE },
      };
      setRunToken((token) => token + 1);

      await new Promise<void>((resolve) => setTimeout(resolve, POP_DELAY_MS));
      if (flightSeqRef.current !== seq) return;
      pulse(opts?.landingPopMagnitude ?? TAKEOVER_PULSE_MAGNITUDE);
      activeRef.current = false;
    },
    [pulse, reducedMotion, x, y]
  );

  /** Fly back to the currently pinned corner — no wind-up, it's mid-choreography already. */
  const returnToCorner = React.useCallback(
    (): Promise<void> => flyTo(anchorFor(cornerRef.current, getViewport(), PUCK_SIZE)),
    [flyTo]
  );

  return {
    x,
    y,
    dragProps: { onDragStart, onDrag, onDragEnd },
    didDragRef,
    accelRef,
    activeRef,
    impulseRef,
    runToken,
    pulse,
    flyTo,
    returnToCorner,
  };
}
